/**
 * English: Knowledge base
 *
 * What this file covers: The knowledge base the chatbot answers from: documents, websites, corrections,
 * conflicts and trying a question.
 *
 * Tone: Plain and patient. Explain what the chatbot will and will not know.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const knowledge: Catalog = {
  'knowledge.title': 'Knowledge base',
  'knowledge.description': 'The documents your AI steps answer from.',
  'knowledge.banner': 'Upload a price list, an FAQ or a policy here. Every AI step answers from all of it unless you tick specific documents, and the assistant below answers questions on its own. See',
  'knowledge.add.title': 'Add a document',
  'knowledge.add.hint': 'PDF, text, Markdown, HTML or CSV, up to 20 MB. The text is split into passages the AI can search.',
  'knowledge.add.paste': 'Paste text instead',
  'knowledge.add.name': 'Name',
  'knowledge.add.namePlaceholder': 'Opening hours and delivery policy',
  'knowledge.add.text': 'Text',
  'knowledge.add.textHint': 'At least 20 characters. Headings and short paragraphs index best.',
  'knowledge.add.save': 'Save document',
  'knowledge.add.reading': 'Reading and indexing…',
  'knowledge.search.title': 'Try a question',
  'knowledge.search.hint': 'Ask as a customer would. You get the answer the assistant would give, and can see what it read.',
  'knowledge.search.placeholder': 'How much is Saturday delivery?',
  'knowledge.search.go': 'Ask',
  'knowledge.search.retriever': '{{count}} passages, via {{retriever}}',
  'knowledge.search.none': 'Nothing in your documents matched. Add a document that covers it.',
  'knowledge.list.search': 'Filter documents',
  'knowledge.list.chunks': 'Passages',
  'knowledge.list.index': 'Index',
  'knowledge.list.notIndexed': 'Not indexed',
  'knowledge.list.reindex': 'Reindex all',
  'knowledge.list.reindexHint': 'Rebuild the search index for every document, for example after changing the embedding model.',
  'knowledge.list.remove': 'Remove {{name}}',
  'knowledge.list.showing': 'Showing {{count}} of {{total}}',
  'knowledge.list.empty': 'No documents yet',
  'knowledge.list.emptyHint': 'Add one above. An AI step with no documents answers from the model alone.',
  'knowledge.modal.removeTitle': 'Remove document',
  'knowledge.modal.removeBody': 'Remove "{{name}}"? AI steps that use it will answer without it.',
  'knowledge.toast.added': '"{{name}}" added ({{chunks}} passages)',
  'knowledge.toast.addFailed': 'Could not add the document',
  'knowledge.toast.removed': '"{{name}}" removed',
  'knowledge.toast.removeFailed': 'Could not remove the document',
  'knowledge.toast.reindexed': 'Reindexed {{documents}} documents ({{chunks}} passages)',
  'knowledge.toast.reindexFailed': 'Could not reindex',
  'knowledge.assistant.title': 'Answer questions automatically',
  'knowledge.assistant.intro': 'People do not follow a flow when they message you. They ask. Switch this on and the bot answers any message that no other flow claims, from every document here. When it is not sure, it asks whether they would like someone from your team to help, and only then asks what they need and how to reach them and tells your team.',
  'knowledge.assistant.on': 'On',
  'knowledge.assistant.off': 'Off',
  'knowledge.assistant.enable': 'Switch on',
  'knowledge.assistant.disable': 'Switch off',
  'knowledge.assistant.openBuilder': 'Open in the builder',
  'knowledge.assistant.noDocuments': 'Add at least one document first, or the bot will have nothing to answer from.',
  'knowledge.assistant.escalationTitle': 'When it cannot answer, tell the team by',
  'knowledge.assistant.email': 'Email',
  'knowledge.assistant.whatsapp': 'WhatsApp number',
  'knowledge.assistant.webhook': 'Web address (your system)',
  'knowledge.assistant.escalationHint': 'Fill in at least one. You can add SMS and change the wording in the builder later.',
  'knowledge.assistant.escalationNone': 'No one is notified yet. Open the flow in the builder and add a way to reach your team on the Notify step.',
  'knowledge.assistant.notifies': 'Requests the bot cannot answer go to:',
  'knowledge.assistant.flowName': 'Knowledge base assistant',
  'knowledge.assistant.otherCatchAll': 'Another flow also answers every message ("{{name}}"). The most recently saved one wins, so switch that one off or give it keywords.',
  'knowledge.assistant.created': 'The assistant is on. Send a message to your number to try it.',
  'knowledge.assistant.updated': 'Assistant {{state}}',
  'knowledge.assistant.prompt': 'Answer only the question asked, in one to three short sentences, from the documents. If the documents hold closely related details the customer may want next, end with one short offer to share them.',
  'knowledge.assistant.unsureMessage': 'I am not certain about that one, but a colleague can help.',
  'knowledge.assistant.askNeed': 'What do you need help with? A sentence or two is perfect.',
  'knowledge.assistant.askName': 'What is your name?',
  'knowledge.assistant.askContact': 'What is the best email address or phone number to reach you on?',
  'knowledge.assistant.subject': 'The bot could not answer {{contact.name ?? contact.msisdn}}',
  'knowledge.assistant.done': 'Thanks {{name}}, someone from the team will be in touch shortly.',
  'knowledge.assistant.notifyName': 'Notify your team',
  'knowledge.assistant.answerName': 'Answer from the knowledge base',
  'knowledge.search.unsure': 'The assistant is not sure about this one. On WhatsApp or the website it would say so, ask what the person needs and notify your team.',
  'knowledge.search.sources': 'What it read ({{count}} passages)',
  'knowledge.search.stubHint': 'No AI provider is set up yet, so a rule-based stand-in answered from the matching sentences. With Bedrock configured, the assistant reads the passages and writes the answer.',
  'knowledge.list.view': 'View {{name}}',
  'knowledge.list.viewFile': 'Open the file this document was read from',
  'knowledge.list.viewText': 'Open the text the assistant reads',
  'knowledge.add.hintMany': 'PDF, text, Markdown, HTML or CSV, up to 20 MB each. Choose several at once: every file is queued as soon as it has uploaded, then indexed in turn, and indexing carries on if you leave this page.',
  'knowledge.add.queued': 'Queued for indexing: {{count}}',
  'knowledge.add.refusedTitle': 'Some files were not added',
  'knowledge.add.refused.PLAN_LIMIT_REACHED': '{{name}}: your plan\'s knowledge base is full. Remove a document or change plan to add more.',
  'knowledge.add.refused.ALREADY_ADDED': '{{name}}: already in your knowledge base.',
  'knowledge.add.refused.NO_TEXT': '{{name}}: no readable text was found. A scanned PDF needs its text recognised before it can be searched.',
  'knowledge.add.refused.MEDIA_NOT_FOUND': '{{name}}: the upload could not be found. Upload it again.',
  'knowledge.add.refused.other': '{{name}}: {{message}}',
  'knowledge.add.sending': 'Adding the uploaded files…',
  'knowledge.list.status': 'Status',
  'knowledge.status.queued': 'Queued',
  'knowledge.status.indexing': 'Indexing',
  'knowledge.status.indexed': 'Indexed',
  'knowledge.status.failed': 'Failed',
  'knowledge.list.retry': 'Try {{name}} again',
  'knowledge.list.pending': 'Indexing {{count}} document(s), one at a time. You can leave this page.',
  'knowledge.toast.retried': '"{{name}}" is queued again',
  'knowledge.toast.retryFailed': 'Could not queue the document again',
  'knowledge.assistant.offerName': 'Offer a colleague',
  'knowledge.assistant.offer': 'Would you like me to pass this to someone on our team?',
  'knowledge.assistant.offerYes': 'Yes',
  'knowledge.assistant.offerNo': 'No',
  'knowledge.assistant.offerRetry': 'Please reply Yes or No.',
  'knowledge.assistant.declined': 'No problem. Ask me anything else whenever you like.',
  'knowledge.search.route.jump': 'On your channels this message would start the flow “{{flow}}” instead of being answered from here.',
  'knowledge.search.route.offer': 'On your channels this answer would go out with a “{{label}}” button for the flow “{{flow}}”.',
  'knowledge.search.route.keyword': 'On your channels the keyword flow “{{flow}}” would start instead.',
  'knowledge.live.title': 'Live sources',
  'knowledge.live.intro': 'Answers that come from your own system instead of a document: event dates, what is available, where an order is. You give the web address once and choose how fresh it has to be.',
  'knowledge.live.add': 'Add a live source',
  'knowledge.live.empty': 'No live sources yet',
  'knowledge.live.emptyHint': 'Add one when an answer changes too often to keep in a document, or depends on who is asking.',
  'knowledge.live.badge': 'Live',
  'knowledge.live.badgeHint': 'The stored copy of a live source. It is kept fresh from a web address; remove the live source to remove it.',
  'knowledge.live.status.new': 'Not fetched yet',
  'knowledge.live.status.ok': 'Working',
  'knowledge.live.status.failing': 'Not working',
  'knowledge.live.status.off': 'Switched off',
  'knowledge.live.modeLine.lookup': 'Fetched when a customer asks',
  'knowledge.live.modeLine.refresh': 'A copy, refreshed {{every}}',
  'knowledge.live.modeLine.manual': 'A copy, refreshed by hand',
  'knowledge.live.every.1': 'every minute',
  'knowledge.live.every.5': 'every 5 minutes',
  'knowledge.live.every.15': 'every 15 minutes',
  'knowledge.live.every.60': 'every hour',
  'knowledge.live.every.1440': 'every day',
  'knowledge.live.every.10080': 'every week',
  'knowledge.live.lastFetched': 'last fetched {{when}}',
  'knowledge.live.lastChanged': 'last changed {{when}}',
  'knowledge.live.fetchNow': 'Fetch now',
  'knowledge.live.fetchNowHint': 'Fetch the address now. Nothing is charged unless what it says has changed.',
  'knowledge.live.remove': 'Remove {{name}}',
  'knowledge.live.removeTitle': 'Remove this live source?',
  'knowledge.live.removeBody': 'The assistant will stop asking “{{name}}” for answers.',
  'knowledge.live.removeBodyCopy': '“{{name}}” and its stored copy are removed, and the assistant stops answering from it.',
  'knowledge.live.toast.added': 'Live source added',
  'knowledge.live.toast.saved': 'Live source saved',
  'knowledge.live.toast.removed': 'Live source removed',
  'knowledge.live.toast.changed': 'Fetched. The answer had changed, so the copy was updated.',
  'knowledge.live.toast.unchanged': 'Fetched. Nothing had changed, so nothing was charged.',
  'knowledge.live.form.new': 'Add a live source',
  'knowledge.live.form.edit': 'Change a live source',
  'knowledge.live.form.name': 'Name',
  'knowledge.live.form.nameHint': 'What you call it, such as Event dates or Orders.',
  'knowledge.live.form.answers': 'What it answers',
  'knowledge.live.form.answersHint': 'In plain words, the way a customer would ask. For example: Where is my order.',
  'knowledge.live.form.examples': 'Example questions',
  'knowledge.live.form.examplesHint': 'One per line. A customer\'s question finds this source by being close to one of these.',
  'knowledge.live.form.mode': 'How fresh it has to be',
  'knowledge.live.mode.lookup': 'Fetch when a customer asks',
  'knowledge.live.mode.refresh': 'Keep a copy and refresh it',
  'knowledge.live.mode.manual': 'Keep a copy, refresh by hand',
  'knowledge.live.cost.lookup': 'Your system is asked at the moment a customer asks, and nothing is stored. An answer costs what an AI answer costs today, plus a small AI step when a detail has to be read from the question.',
  'knowledge.live.cost.refresh': 'Charged once as a document when you add it. After that it is checked up to {{checks}} times a day, and a check is charged only when the answer has changed, as the reading of the new text.',
  'knowledge.live.cost.manual': 'Charged once as a document when you add it. After that it changes only when you press Fetch now, and that is charged only when the answer has changed.',
  'knowledge.live.form.interval': 'Refresh',
  'knowledge.live.form.method': 'Method',
  'knowledge.live.form.url': 'Web address',
  'knowledge.live.form.urlHint': 'The address that returns the information.',
  'knowledge.live.form.urlHintLookup': 'Put a detail from the question in double braces where it belongs, such as {{order}}.',
  'knowledge.live.form.body': 'What to send',
  'knowledge.live.form.bodyHint': 'The body of the request, usually JSON. Details from the question may be used in double braces.',
  'knowledge.live.form.secretHint': 'Stored encrypted and never shown again. To change it, type a new one.',
  'knowledge.live.form.secretSaved': 'Saved. Leave empty to keep it.',
  'knowledge.live.form.details': 'Details from the question',
  'knowledge.live.form.detailsHint': 'What the assistant must take from the customer\'s message before it can ask your system, such as an order number. If the customer has not given one, the assistant asks for it.',
  'knowledge.live.form.detailName': 'Short name',
  'knowledge.live.form.detailDescription': 'What it is',
  'knowledge.live.form.detailPlaceholder': 'the order number',
  'knowledge.live.form.detailAdd': 'Add a detail',
  'knowledge.live.form.detailRemove': 'Remove this detail',
  'knowledge.live.form.paths': 'What to read from the response',
  'knowledge.live.form.pathsHint': 'Leave empty to read all of it. To read only some parts, run the test and choose them from the list.',
  'knowledge.live.form.pathPath': 'Part of the response',
  'knowledge.live.form.pathLabel': 'What to call it',
  'knowledge.live.form.pathLabelPlaceholder': 'Status',
  'knowledge.live.form.pathAdd': 'Add a part',
  'knowledge.live.form.pathRemove': 'Remove this part',
  'knowledge.live.form.active': 'Switched on',
  'knowledge.live.form.activeHint': 'Switched off, it is neither asked nor refreshed.',
  'knowledge.live.test.title': 'Test',
  'knowledge.live.test.hint': 'Calls the address once and shows what the assistant would read. Nothing is saved or charged.',
  'knowledge.live.test.hintLookup': 'Type a customer\'s question to see whether it finds this source, which details are taken from it, and what the assistant would read. Nothing is saved or charged.',
  'knowledge.live.test.question': 'A customer\'s question',
  'knowledge.live.test.questionPlaceholder': 'Where is my order 12345?',
  'knowledge.live.test.run': 'Run the test',
  'knowledge.live.test.matched': 'This question would find the source ({{score}}% match; {{needed}}% is needed).',
  'knowledge.live.test.notMatched': 'This question would not find the source ({{score}}% match; {{needed}}% is needed). Add an example question closer to it.',
  'knowledge.live.test.values': 'Taken from the question: {{values}}',
  'knowledge.live.test.missing': 'Not in the question: {{names}}. The assistant would ask for it.',
  'knowledge.live.test.passage': 'What the assistant would read',
  'knowledge.live.test.paths': 'Parts of the response ({{count}}). Choose one to read only that.',
  'knowledge.web.title': 'Websites',
  'knowledge.web.intro': 'Give your website\'s address and your assistant learns from its pages. We look at the site first and suggest the pages worth reading; each page you choose becomes one document. Read it again whenever the site changes: unchanged pages cost nothing.',
  'knowledge.web.address': 'Website address',
  'knowledge.web.addressHint': 'Your own public website, such as www.example.co.za. Only pages on that site are read.',
  'knowledge.web.analyse': 'Look at the website',
  'knowledge.web.empty': 'No websites yet',
  'knowledge.web.emptyHint': 'Add your website and we will suggest which pages to read.',
  'knowledge.web.status.new': 'Waiting',
  'knowledge.web.status.analysing': 'Looking at the site',
  'knowledge.web.status.analysed': 'Pages to choose',
  'knowledge.web.status.reading': 'Reading',
  'knowledge.web.status.ready': 'In the knowledge base',
  'knowledge.web.status.failing': 'Needs attention',
  'knowledge.web.pagesIn': '{{count}} pages in the knowledge base',
  'knowledge.web.lastRead': 'last read {{when}}',
  'knowledge.web.foundLine': '{{found}} pages found, {{suggested}} suggested',
  'knowledge.web.newPages': '{{count}} new pages found on the site. Open Pages to add them.',
  'knowledge.web.pages': 'Pages',
  'knowledge.web.readAgain': 'Read the website again',
  'knowledge.web.remove': 'Remove {{name}}',
  'knowledge.web.removeTitle': 'Remove this website?',
  'knowledge.web.removeBody': '{{name}} and its {{count}} pages leave the knowledge base. Your assistant stops answering from them.',
  'knowledge.web.toast.reading': 'Reading the pages now. This takes a few minutes.',
  'knowledge.web.toast.removed': 'Website removed from the knowledge base.',
  'knowledge.web.busy.analysing': 'Looking at the site to find its pages. This takes a few minutes.',
  'knowledge.web.busy.reading': 'Reading the chosen pages into the knowledge base. This takes a few minutes.',
  'knowledge.web.suggest.room': '{{worth}} pages are worth reading. Your plan has room for {{room}}, so we suggest these {{suggested}}.',
  'knowledge.web.suggest.unlimited': '{{worth}} pages are worth reading. We suggest all {{suggested}}.',
  'knowledge.web.suggest.why': 'We suggest the main pages first, then the pages that say the most. Pages with very little text, copies of other pages, and login or basket pages are left out.',
  'knowledge.web.suggest.incomplete': 'The site has more pages than we look at in one go (300). Add the most important pages first.',
  'knowledge.web.overRoom': 'You have chosen more pages than your plan has room for ({{room}}). Untick some, or choose a bigger plan.',
  'knowledge.web.lastCounts': 'Last read: {{added}} added, {{updated}} updated, {{unchanged}} unchanged, {{removed}} removed, {{failed}} could not be read.',
  'knowledge.web.worthTitle': 'Pages worth reading ({{count}})',
  'knowledge.web.useSuggestion': 'Use our suggestion',
  'knowledge.web.clear': 'Clear',
  'knowledge.web.new': 'new',
  'knowledge.web.suggested': 'suggested',
  'knowledge.web.words': '{{count}} words',
  'knowledge.web.othersTitle': 'Pages left out ({{count}})',
  'knowledge.web.reason.few_words': 'very little text',
  'knowledge.web.reason.duplicate': 'the same as another page',
  'knowledge.web.reason.private': 'a login, account, basket or search page',
  'knowledge.web.reason.not_page': 'not a page',
  'knowledge.web.reason.noindex': 'the page asks not to be read',
  'knowledge.web.reason.robots': 'the site asks not to be read here',
  'knowledge.web.reason.failed': 'could not be opened',
  'knowledge.web.reason.skipped': 'in a part of the site you skipped',
  'knowledge.web.reason.no_room': 'no room on your plan',
  'knowledge.web.jsHint': 'Pages that only show their text once a browser has run them have nothing for us to read. For those, upload a PDF of the page instead.',
  'knowledge.web.readChosen': 'Read {{count}} pages',
  'knowledge.web.readChosenAgain': 'Save and read {{count}} pages',
  'knowledge.web.badge': 'Website',
  'knowledge.web.badgeHint': 'A page of your website',
  'knowledge.conflicts.title': 'Conflicting information',
  'knowledge.conflicts.intro': 'When two documents say different things, the bot could give the wrong one. Each new or changed document is compared with the others that cover the same ground, charged as AI use (skipped when your credit cannot cover it). Settle a conflict by writing the correct answer: the bot then always uses it.',
  'knowledge.conflicts.checkAll': 'Check everything',
  'knowledge.conflicts.checking': 'Checking for conflicting information…',
  'knowledge.conflicts.lastChecked': 'Last checked {{when}}.',
  'knowledge.conflicts.skipped': 'The check on {{when}} was skipped: not enough credit. Top up, then press Check everything.',
  'knowledge.conflicts.failed': 'The check on {{when}} could not finish. Press Check everything to try again.',
  'knowledge.conflicts.empty': 'No conflicting information found',
  'knowledge.conflicts.emptyHint': 'Documents that disagree on a time, a price, an address or a rule show here.',
  'knowledge.conflicts.removedDocument': 'A removed document',
  'knowledge.conflicts.correct': 'Write the correct answer',
  'knowledge.conflicts.dismiss': 'Not a conflict',
  'knowledge.conflicts.dismissed': 'Marked as not a conflict. It will not be raised again.',
  'knowledge.conflicts.started': 'Checking your knowledge base. Conflicts show here as they are found.',
  'knowledge.conflicts.nothing': 'No two documents cover the same ground, so there is nothing to compare.',
  'knowledge.conflicts.confirmTitle': 'Check everything for conflicting information?',
  'knowledge.conflicts.confirmBody': '{{pairs}} pairs of documents that cover the same ground will be compared ({{documents}} documents).',
  'knowledge.conflicts.cost': 'About {{amount}}, charged as AI use.',
  'knowledge.conflicts.costUnknown': 'Charged as AI use. The cost could not be worked out just now.',
  'knowledge.conflicts.notEnough': 'There is not enough credit for this check. Top up first.',
  'knowledge.conflicts.run': 'Check',
  'knowledge.corrections.title': 'Corrections',
  'knowledge.corrections.intro': 'Type the right information here and the bot puts it ahead of every document, for example when a document is out of date or two documents disagree.',
  'knowledge.corrections.add': 'Add a correction',
  'knowledge.corrections.empty': 'No corrections yet',
  'knowledge.corrections.emptyHint': 'Add one when the bot should give something different from what a document says.',
  'knowledge.corrections.inUse': 'Use the correction {{title}}',
  'knowledge.corrections.remove': 'Remove the correction {{title}}',
  'knowledge.corrections.removeTitle': 'Remove this correction?',
  'knowledge.corrections.removeBody': 'The bot goes back to what the documents say about {{title}}.',
  'knowledge.corrections.removed': 'Correction removed.',
  'knowledge.corrections.addTitle': 'Add a correction',
  'knowledge.corrections.editTitle': 'Change the correction',
  'knowledge.corrections.conflictTitle': 'Write the correct answer',
  'knowledge.corrections.conflictIntro': 'The bot will give this whenever the question is about this topic, whatever the documents say. The conflict is then settled.',
  'knowledge.corrections.about': 'What is it about?',
  'knowledge.corrections.aboutHint': 'A few words, such as Sunday service times.',
  'knowledge.corrections.answer': 'The correct information',
  'knowledge.corrections.answerHint': 'Write it the way the bot should say it.',
  'knowledge.corrections.save': 'Save',
  'knowledge.corrections.saved': 'Saved. The bot uses it from the next question.',
  'knowledge.corrections.savedConflict': 'Saved, and the conflict is settled. The bot uses it from the next question.',
  'knowledge.conflicts.confirmBodyOne': 'One pair of documents that cover the same ground will be compared.',
  'knowledge.tabs.documents': 'Documents',
  'knowledge.tabs.websites': 'Websites',
  'knowledge.tabs.live': 'Live sources',
  'knowledge.tabs.try': 'Try a question',
  'knowledge.tabs.conflicts': 'Conflicts',
  'knowledge.tabs.corrections': 'Corrections',
  'knowledge.tabs.chatbot': 'Answering on its own',
  'knowledge.tabs.attention': '{{count}} need your attention',
  'knowledge.search.action.book': 'Your assistant would start a booking here: it offers the services and free times, then books.',
  'knowledge.search.action.manage_booking': 'Your assistant would help with the customer\'s existing booking here: confirm, move or cancel it.',
  'knowledge.search.action.new_ticket': 'Your assistant would log a request for your team here and give the customer its number.',
  'knowledge.search.bookingFacts': 'Your bookings and requests',
  'knowledge.live.addByHand': 'Add by hand',
  'knowledge.api.open': 'Add from API documentation',
  'knowledge.api.via': 'Signs in through {{name}}',
  'knowledge.api.title': 'Add from API documentation',
  'knowledge.api.titleMore': 'Add more from {{name}}',
  'knowledge.api.step.docs': 'Documentation',
  'knowledge.api.step.connect': 'Sign in',
  'knowledge.api.step.choose': 'Choose',
  'knowledge.api.step.review': 'Review',
  'knowledge.api.back': 'Back',
  'knowledge.api.docs.intro': 'Give the documentation of your system\'s API. The endpoints are listed, you sign in once, and you choose which ones the chatbot may use.',
  'knowledge.api.docs.drop': 'Drop the documentation here, or click to choose a file',
  'knowledge.api.docs.kinds': 'OpenAPI or Swagger (JSON or YAML), a Postman collection, Markdown or a PDF',
  'knowledge.api.docs.paste': 'Or paste it',
  'knowledge.api.docs.pasteHint': 'Paste an OpenAPI description, a Postman collection or the documentation\'s text.',
  'knowledge.api.docs.cost': 'OpenAPI, Swagger and Postman files are read free. Markdown and PDF files are read by AI and charged as AI use from your credit, usually 1 to 4 credits.',
  'knowledge.api.docs.reading': 'Reading the documentation. A long PDF can take up to a minute.',
  'knowledge.api.docs.read': 'Read the documentation',
  'knowledge.api.kind.openapi': 'an OpenAPI description',
  'knowledge.api.kind.postman': 'a Postman collection',
  'knowledge.api.kind.markdown': 'the text',
  'knowledge.api.kind.pdf': 'the PDF',
  'knowledge.api.kind.manual': 'the documentation',
  'knowledge.api.connect.found': 'Endpoints found in {{kind}}: {{count}}.',
  'knowledge.api.connect.foundAi': 'Endpoints the AI found in {{kind}}: {{count}}. Check them when you choose.',
  'knowledge.api.connect.shortened': 'The documentation was long, so only its first part was read.',
  'knowledge.api.connect.name': 'Name',
  'knowledge.api.connect.nameHint': 'How this API is named on this page and in flows.',
  'knowledge.api.connect.baseUrl': 'Base address',
  'knowledge.api.connect.baseUrlHint': 'Every endpoint is a path on this address.',
  'knowledge.api.connect.secretHint': 'Stored encrypted and never shown again. Every live source and flow step on this API uses it, so a new key is changed in one place.',
  'knowledge.api.connect.docsSay': 'The documentation says: {{note}}',
  'knowledge.api.connect.tryTitle': 'Try the sign-in',
  'knowledge.api.connect.tryHint': 'Calls one endpoint that only reads, to check the address and the sign-in. An endpoint that does something is never called here.',
  'knowledge.api.connect.nothingToTry': 'This documentation has no endpoint that only reads, so there is nothing safe to try.',
  'knowledge.api.connect.tryWhich': 'Endpoint to try',
  'knowledge.api.connect.try': 'Try it',
  'knowledge.api.connect.tryOk': 'It answered ({{status}}).',
  'knowledge.api.connect.tryMissing': 'Give a value for: {{names}}',
  'knowledge.api.connect.next': 'Choose endpoints',
  'knowledge.api.choose.intro': 'Tick what the chatbot may use. Endpoints that answer become live sources; endpoints that do something are kept for flows.',
  'knowledge.api.choose.places': 'Live sources your plan still has room for: {{count}}.',
  'knowledge.api.choose.noPlaces': 'Your plan has no room for more live sources. You can still add actions for flows, or upgrade your plan.',
  'knowledge.api.choose.filter': 'Find an endpoint',
  'knowledge.api.choose.reads': 'Answers questions',
  'knowledge.api.choose.readsHint': 'Each one becomes a live source and takes one place in your plan\'s knowledge. Choose up to {{max}} at a time.',
  'knowledge.api.choose.actions': 'Does something',
  'knowledge.api.choose.actionsHint': 'Kept as actions for a flow\'s HTTP request step, such as making a booking. They are never called on their own.',
  'knowledge.api.choose.draftCost': 'The AI drafts each one from the documentation and a real answer, charged as AI use from your credit, usually under 1 credit.',
  'knowledge.api.choose.drafting': 'Trying each endpoint and drafting it.',
  'knowledge.api.choose.draft': 'Draft with AI',
  'knowledge.api.choose.review': 'Review',
  'knowledge.api.review.addSource': 'Add {{name}}',
  'knowledge.api.review.tried': 'Tried: it answered',
  'knowledge.api.review.triedFailed': 'Tried: {{error}}',
  'knowledge.api.review.notTried': 'Not tried: it needs a detail with no example',
  'knowledge.api.review.key': 'Name in flows',
  'knowledge.api.review.keyHint': 'A flow\'s Look up step reads its data points as {{example}}.',
  'knowledge.api.review.lookupOnly': 'It takes details from the customer\'s question, so it is asked when a customer asks.',
  'knowledge.api.review.detailsHint': 'Say what each detail is in the customer\'s words, so the assistant knows what to look for in a question.',
  'knowledge.api.review.points': 'Data points',
  'knowledge.api.review.pointsHint': 'The parts of the answer the assistant reads and flows can use. Give each a plain name.',
  'knowledge.api.review.noPoints': 'The documentation does not say what this endpoint answers, so all of its answer is read.',
  'knowledge.api.review.pointKey': 'Name in flows',
  'knowledge.api.review.sample': 'A real answer',
  'knowledge.api.review.actions': 'Actions for flows',
  'knowledge.api.review.actionsHint': 'Choose one in a flow\'s HTTP request step: the address and sign-in come from this connection.',
  'knowledge.api.review.add': 'Add live sources: {{sources}} · actions: {{actions}}',
  'knowledge.api.toast.added': 'Live sources added: {{sources}} · actions: {{actions}}',
  'knowledge.api.list.title': 'API connections',
  'knowledge.api.list.counts': 'live sources: {{sources}} · actions: {{actions}}',
  'knowledge.api.list.actions': 'Actions ({{count}})',
  'knowledge.api.list.removeAction': 'Remove the action {{name}}',
  'knowledge.api.list.actionsHint': 'Flow steps already made from an action keep working when it is removed.',
  'knowledge.api.list.addMore': 'Add more',
  'knowledge.api.list.signIn': 'Sign-in',
  'knowledge.api.list.remove': 'Remove {{name}}',
  'knowledge.api.list.removeTitle': 'Remove this API connection?',
  'knowledge.api.list.removeBody': '{{name}} is removed with its live sources ({{sources}}) and actions ({{actions}}). Flow steps that use it stop working and take their error path.',
  'knowledge.api.list.removed': 'API connection removed',
  'knowledge.api.list.actionRemoved': 'Action removed',
  'knowledge.api.list.saved': 'API connection saved',
  'knowledge.api.list.signInTitle': 'API connection',
  'knowledge.api.list.signInHint': 'A change reaches every live source, action and flow step on this connection from its next call. Leave a saved secret empty to keep it.',
  'knowledge.api.form.path': 'Path',
  'knowledge.api.form.pathHint': 'A path on {{base}}. Use {{example}} for each detail from the question.',
  'knowledge.api.form.signsIn': 'Signs in through the API connection {{name}}. Change its key under API connections.',
  'knowledge.api.form.keyHint': 'Optional. A flow\'s Look up step keeps what this source reads under this name.',
  'knowledge.api.status.new': 'Not tried yet',
  'knowledge.api.status.ok': 'Working',
  'knowledge.api.status.failing': 'Not working',
  'knowledge.conflicts.chooseIntro': 'Choose a check. Each is charged as AI use, and only when you start it.',
  'knowledge.conflicts.quick.title': 'Quick check',
  'knowledge.conflicts.quick.about': 'Compares the closest passages of documents that cover the same ground.',
  'knowledge.conflicts.quick.run': 'Start the quick check',
  'knowledge.conflicts.deep.title': 'Deep check',
  'knowledge.conflicts.deep.about': 'Reads every fact in every document and correction, and compares all the facts about the same thing, so a fee or a date is found wherever it is written.',
  'knowledge.conflicts.deep.toRead': 'To read now: {{count}} documents and corrections.',
  'knowledge.conflicts.deep.toReadOne': 'To read now: one document or correction.',
  'knowledge.conflicts.deep.toReadNone': 'Nothing new to read: every document and correction was read before.',
  'knowledge.conflicts.deep.facts': 'Facts already known: {{count}}.',
  'knowledge.conflicts.deep.groups': 'Groups of facts to compare: {{count}}.',
  'knowledge.conflicts.deep.groupsAbout': 'Groups of facts to compare: about {{count}}.',
  'knowledge.conflicts.deep.models': 'Claude Haiku reads every fact and Claude Sonnet judges what disagrees.',
  'knowledge.conflicts.deep.modelsFallback': 'Claude Haiku reads every fact and also judges what disagrees, because Claude Sonnet is switched off.',
  'knowledge.conflicts.deep.reuse': 'Documents and corrections read before are not read again, so a check after small changes costs little.',
  'knowledge.conflicts.deep.capped': 'Your knowledge base is larger than one deep check reads. Run it again afterwards to carry on where it stopped.',
  'knowledge.conflicts.deep.nothing': 'The deep check needs at least two documents or corrections.',
  'knowledge.conflicts.deep.unavailable': 'The deep check could not be priced just now. Try again shortly.',
  'knowledge.conflicts.deep.run': 'Start the deep check',
  'knowledge.conflicts.startedDeep': 'The deep check has started. It reads first, then compares; conflicts show here as soon as it is done.',
  'knowledge.conflicts.checkingDeep': 'Running a deep check…',
  'knowledge.conflicts.progressReading': 'Deep check: {{read}} of {{total}} documents and corrections read, {{facts}} facts found.',
  'knowledge.conflicts.progressJudging': 'Deep check: {{read}} of {{total}} documents and corrections read, {{facts}} facts found, {{judged}} of {{groups}} groups compared.',
  'knowledge.conflicts.progressComparing': 'Deep check: {{judged}} of {{groups}} groups of facts compared.',
  'knowledge.conflicts.lastCheckedQuick': 'Last quick check {{when}}.',
  'knowledge.conflicts.lastCheckedDeep': 'Last deep check {{when}}.',
  'knowledge.conflicts.failedDeep': 'The deep check on {{when}} could not finish. Press Check everything to try again.',
  'knowledge.conflicts.foundByDeep': 'Deep check',
  'knowledge.conflicts.quote': '“{{quote}}”',
  'knowledge.conflicts.reason': 'Why they disagree: {{reason}}',
  'knowledge.conflicts.yourCorrection': 'Your correction, {{title}}',
  'knowledge.conflicts.correctionHint': 'The bot already answers from your correction. Update or remove this document so that it says the same.',
  'knowledge.conflicts.openDocument': 'Open the document'
};

export default knowledge;
